/**
 * Infinite Scroll Utility
 *
 * Uses the Intersection Observer API to detect when the user scrolls near
 * the bottom of a container and triggers loading of more content.
 */

/**
 * Options for configuring the infinite scroll behavior
 */
export interface InfiniteScrollOptions {
  /** Callback function to load more items */
  onLoadMore: () => Promise<void>;
  /** Root margin for the Intersection Observer (default: '100px') */
  rootMargin?: string;
  /** Threshold for intersection (default: 0.1) */
  threshold?: number;
  /** Whether infinite scroll is enabled (default: true) */
  enabled?: boolean;
}

/**
 * State of the infinite scroll
 */
export interface InfiniteScrollState {
  /** Whether more items are being loaded */
  isLoading: boolean;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether there was an error loading more items */
  hasError: boolean;
  /** Error message if there was an error */
  errorMessage?: string;
}

/**
 * Result of the useInfiniteScroll function
 */
export interface InfiniteScrollResult {
  /** The sentinel element that triggers loading */
  sentinel: HTMLElement;
  /** Update the state of the infinite scroll */
  setState: (state: Partial<InfiniteScrollState>) => void;
  /** Get the current state */
  getState: () => InfiniteScrollState;
  /** Enable or disable the infinite scroll */
  setEnabled: (enabled: boolean) => void;
  /** Retry loading after an error */
  retry: () => void;
  /** Reset the infinite scroll state */
  reset: () => void;
  /** Destroy the infinite scroll (cleanup) */
  destroy: () => void;
}

/**
 * Create an infinite scroll controller using Intersection Observer
 *
 * @param options - Configuration options
 * @returns InfiniteScrollResult with sentinel element and control functions
 *
 * @example
 * ```ts
 * const infiniteScroll = createInfiniteScroll({
 *   onLoadMore: async () => {
 *     const newItems = await fetchMoreItems();
 *     appendItems(newItems);
 *     infiniteScroll.setState({ hasMore: newItems.length > 0 });
 *   }
 * });
 *
 * // Add sentinel to the end of your list
 * listContainer.appendChild(infiniteScroll.sentinel);
 * ```
 */
export function createInfiniteScroll(options: InfiniteScrollOptions): InfiniteScrollResult {
  const {
    onLoadMore,
    rootMargin = '100px',
    threshold = 0.1,
    enabled: initialEnabled = true,
  } = options;

  // State - using a function to create fresh state objects
  const createInitialState = (): InfiniteScrollState => ({
    isLoading: false,
    hasMore: true,
    hasError: false,
  });

  let state: InfiniteScrollState = createInitialState();

  let enabled = initialEnabled;
  let observer: IntersectionObserver | null = null;

  // Create sentinel element
  const sentinel = document.createElement('div');
  sentinel.className = 'infinite-scroll-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.height = '1px';
  sentinel.style.width = '100%';

  /**
   * Handle intersection
   */
  const handleIntersection = async (entries: IntersectionObserverEntry[]) => {
    const entry = entries[0];
    if (!entry) return;

    if (
      entry.isIntersecting &&
      enabled &&
      !state.isLoading &&
      state.hasMore &&
      !state.hasError
    ) {
      state.isLoading = true;
      updateSentinelContent();

      try {
        await onLoadMore();
        state.hasError = false;
        delete state.errorMessage;
      } catch (error) {
        state.hasError = true;
        state.errorMessage = error instanceof Error ? error.message : 'Failed to load more items';
      } finally {
        state.isLoading = false;
        updateSentinelContent();
      }
    }
  };

  /**
   * Update sentinel content based on state
   */
  const updateSentinelContent = () => {
    sentinel.innerHTML = '';
    sentinel.className = 'infinite-scroll-sentinel';

    if (state.isLoading) {
      sentinel.className = 'infinite-scroll-sentinel flex justify-center py-6';
      sentinel.innerHTML = `
        <div class="flex items-center gap-2 text-muted-foreground" role="status" aria-label="Loading more items">
          <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
          </svg>
          <span class="text-sm">Loading more...</span>
        </div>
      `;
    } else if (state.hasError) {
      sentinel.className = 'infinite-scroll-sentinel flex justify-center py-6';
      sentinel.innerHTML = `
        <div class="flex flex-col items-center gap-2 text-destructive">
          <p class="text-sm">${escapeHtml(state.errorMessage || 'Failed to load more items')}</p>
          <button type="button" class="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            Try Again
          </button>
        </div>
      `;
      const retryButton = sentinel.querySelector('button');
      retryButton?.addEventListener('click', () => {
        state.hasError = false;
        delete state.errorMessage;
        updateSentinelContent();
        handleIntersection([{ isIntersecting: true } as IntersectionObserverEntry]);
      });
    } else if (!state.hasMore) {
      // End of list - keep sentinel minimal with no visible content
      sentinel.className = 'infinite-scroll-sentinel';
      sentinel.innerHTML = '';
    }
  };

  /**
   * Create and setup the Intersection Observer
   */
  const setupObserver = () => {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold,
    });

    observer.observe(sentinel);
  };

  // Setup observer
  setupObserver();

  return {
    sentinel,

    setState: (newState: Partial<InfiniteScrollState>) => {
      state = { ...state, ...newState };
      updateSentinelContent();
    },

    getState: () => ({ ...state }),

    setEnabled: (newEnabled: boolean) => {
      enabled = newEnabled;
    },

    retry: () => {
      state.hasError = false;
      delete state.errorMessage;
      updateSentinelContent();
      handleIntersection([{ isIntersecting: true } as IntersectionObserverEntry]);
    },

    reset: () => {
      state = createInitialState();
      enabled = true;
      updateSentinelContent();
    },

    destroy: () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      sentinel.remove();
    },
  };
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create a scroll-to-top button that appears when scrolling down
 *
 * @param options - Configuration options
 * @returns The button element and control functions
 *
 * @example
 * ```ts
 * const scrollToTop = createScrollToTopButton({
 *   scrollContainer: document.querySelector('.scroll-container')
 * });
 * document.body.appendChild(scrollToTop.button);
 * ```
 */
export interface ScrollToTopOptions {
  /** The scroll container to watch (defaults to window) */
  scrollContainer?: HTMLElement | null;
  /** Threshold in pixels before showing the button (default: 400) */
  threshold?: number;
  /** Smooth scroll behavior (default: true) */
  smooth?: boolean;
}

export interface ScrollToTopResult {
  /** The button element */
  button: HTMLButtonElement;
  /** Show the button */
  show: () => void;
  /** Hide the button */
  hide: () => void;
  /** Destroy the scroll-to-top functionality */
  destroy: () => void;
}

export function createScrollToTopButton(options: ScrollToTopOptions = {}): ScrollToTopResult {
  const {
    scrollContainer = null,
    threshold = 400,
    smooth = true,
  } = options;

  // Create button
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'scroll-to-top fixed bottom-6 right-6 z-50 p-3 bg-primary text-primary-foreground rounded-full shadow-lg transition-all duration-300 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50';
  button.style.transform = 'scale(0)';
  button.style.opacity = '0';
  button.setAttribute('aria-label', 'Scroll to top');
  button.innerHTML = `
    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
  `;

  let isVisible = false;

  /**
   * Show the button with animation
   */
  const show = () => {
    if (!isVisible) {
      isVisible = true;
      button.style.transform = 'scale(1)';
      button.style.opacity = '1';
    }
  };

  /**
   * Hide the button with animation
   */
  const hide = () => {
    if (isVisible) {
      isVisible = false;
      button.style.transform = 'scale(0)';
      button.style.opacity = '0';
    }
  };

  /**
   * Handle scroll events
   */
  const handleScroll = () => {
    const scrollTop = scrollContainer
      ? scrollContainer.scrollTop
      : window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > threshold) {
      show();
    } else {
      hide();
    }
  };

  /**
   * Scroll to top
   */
  const scrollToTop = () => {
    const scrollBehavior = smooth ? 'smooth' : 'auto';

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: scrollBehavior });
    } else {
      window.scrollTo({ top: 0, behavior: scrollBehavior });
    }
  };

  // Setup event listeners
  const scrollTarget = scrollContainer || window;
  scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
  button.addEventListener('click', scrollToTop);

  // Initial check
  handleScroll();

  return {
    button,
    show,
    hide,
    destroy: () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
      button.removeEventListener('click', scrollToTop);
      button.remove();
    },
  };
}

export default createInfiniteScroll;
